/**
 * sedStaleness — pure helper that classifies each SED section as
 * fresh / stale / missing based on a per-section TTL.
 *
 * Drives the new "Refresh evidence" popover on SiteLandingPage so
 * the user can see at a glance which sections need a re-fetch and
 * scope the refresh to those only. Architecture is unchanged — the
 * site still owns one canonical SED; this module is a presentation
 * layer over the existing per-section retrievedAt timestamps.
 *
 * Side-effect free so the section list is testable without
 * mounting the UI or the store.
 */
import type { EvidenceSection, SedSectionId, SiteEvidenceDocument } from '../types/siteEvidence';
import { PUBLIC_SENTIMENT_SCAN_MAX_AGE_DAYS } from './sentiment/sentimentScanAge';

export type SedSectionStaleness = 'fresh' | 'stale' | 'missing';

export interface SedSectionStatus {
  id: SedSectionId;
  /** Human-readable label for the popover row. */
  label: string;
  /** ISO timestamp the section was last fetched, when known. */
  lastFetchedAt?: string;
  /** Whole-day age. Undefined when the section has no data. */
  ageDays?: number;
  /** Days after which the section is considered stale. */
  ttlDays: number;
  staleness: SedSectionStaleness;
  /** Provider ids that feed this section (empty for AI-synthesised
   *  surfaces like siteResearch). The store action maps section
   *  selections to providers via this list. */
  providers: string[];
}

/**
 * Per-section TTLs in days. Tuned for cadence of source-of-truth
 * updates: AQI changes hourly so its TTL is short; seismic hazard
 * curves are stable for years so its TTL is long.
 *
 * Sections that aren't user-refreshable (siteResearch, adamsDocuments,
 * siteProfile — driven by Gemini synthesis or one-shot derivations)
 * still get a TTL so the popover can flag them, even if the refresh
 * itself routes through different machinery.
 */
export const SED_SECTION_TTL_DAYS: Record<SedSectionId, number> = {
  climate:         30,
  waterStress:     90,
  seismic:        365,
  geotechnical:   365,
  grid:             7,
  flood:           90,
  environmental:   30,
  nuclearInfra:    90,
  solar:           90,
  wind:            90,
  electricity:     14,
  airQuality:       1,
  connectivity:    14,
  parcel:          90,
  regulatory:      30,
  siteResearch:    30,
  adamsDocuments:  90,
  siteProfile:    365,
  // KSD1 — KMZ-derived facts. The KMZ itself doesn't go stale unless
  // the user uploads a new one or edits the boundary; a long TTL
  // matches the cadence at which site-truth shifts on the ground.
  onSiteInfrastructure: 365,
  // LL1 — live literature retrieval. 14-day TTL mirrors the per-query
  // Firestore cache the pack ships; new papers land continuously so a
  // 2-week refresh keeps the section reasonably current without
  // hammering the upstream APIs.
  externalLiterature: 14,
  // PSE-1 — public sentiment retrieval. The ONE sentiment-scan age limit
  // (sentimentScanAge.ts, owner decision 2026-09-30: reuse a recent scan
  // instead of rescanning): a dated scan up to 7 days old is an admissible
  // screening basis, and every report states the scan's own collection date.
  // The former 1-day TTL flagged a 2-day-old scan stale here, so the rerun
  // panel's "Refresh stale evidence" and the refresh banner re-scanned (and
  // re-published the SED) long before any gate needed a new scan.
  publicSentiment: PUBLIC_SENTIMENT_SCAN_MAX_AGE_DAYS,
  technology:       365,
  // v1.2.1 HR-5 — intent-stamped BTM sections track the intent (long TTL — they
  // refresh on intent edit, not a provider clock). fuelSupply tracks the EIA
  // natural-gas price (monthly state series; 14-day TTL like electricity).
  cooling:          365,
  power:            365,
  firmCapacity:     365,
  lcoe:             365,
  fuelSupply:        14,
  // SED D2 — interconnection tracks the ISO queue (weekly-refresh public
  // dumps; 7-day TTL like grid). incentives is intent/financial-stamped (IRA
  // statute changes annually; 365-day TTL).
  interconnection:    7,
  incentives:       365,
  // SED D2 (H-6) — multiHazard tracks the FEMA NRI annual release (county
  // dataset; 365-day TTL like seismic). transmissionHeadroom + waterResource
  // are honest-unavailable typed sections today; a long TTL avoids churning
  // the empty section (they only flip when a real fetcher lands).
  multiHazard:        365,
  transmissionHeadroom: 365,
  waterResource:      365,
  topography:          365,
  survey:              365,
  mineralResources:     90,
  culturalResources:    90,
  // Server-authored workforce/screening/comparable snapshots. Snapshot
  // revision drift triggers a full rebuild; the clock is a secondary nudge.
  economicEvidence:     30,
};

/** Per-section provider ids — mirrors SECTION_TO_PROVIDER_ID in
 *  sedBootstrap.ts. Surfaced through SedSectionStatus so the
 *  refresh popover and the store action speak the same provider
 *  list without duplicating the mapping. */
export const SED_SECTION_PROVIDERS: Record<SedSectionId, string[]> = {
  climate:        ['open-meteo'],
  waterStress:    ['wri-aqueduct'],
  seismic:        ['usgs-seismic'],
  geotechnical:   ['nrcs-soil-survey'],
  grid:           ['hifld-grid'],
  flood:          ['fema-flood'],
  environmental:  ['usfws-env'],
  nuclearInfra:   ['nrc-reactors'],
  solar:          ['nrel-solar'],
  wind:           ['nrel-wind'],
  electricity:    ['eia-electricity'],
  airQuality:     ['airnow-aqi'],
  connectivity:   ['peeringdb-ix'],
  parcel:         [],   // derived; no live provider
  regulatory:     ['regulatory-db', 'jurisdiction-boundaries'],
  siteResearch:   ['site-research'],
  adamsDocuments: [],   // ADAMS fetched as part of siteResearch
  siteProfile:    [],   // computed locally
  onSiteInfrastructure: [],   // KSD2 — derived from site.siteLayers, no live provider
  externalLiterature: ['external-literature'],   // LL1 — synthetic orchestrator (crossref + arxiv + pubmed)
  publicSentiment: ['public-sentiment'],         // PSE-1 — synthetic orchestrator (gdelt + local-minutes + federal-dockets)
  technology:     [],   // intent-stamped from Project Intent; no live provider
  // v1.2.1 HR-5 — cooling / power / firmCapacity / lcoe are intent-stamped (no
  // live provider); fuelSupply is grounded by the EIA natural-gas provider.
  cooling:        [],
  power:          [],
  firmCapacity:   [],
  lcoe:           [],
  fuelSupply:     ['eia-natural-gas'],
  // SED D2 — interconnection is grounded by the deterministic iso-queue
  // provider; incentives is intent/financial-stamped (no live provider).
  interconnection: ['iso-queue'],
  incentives:     [],
  // SED D2 (H-6) — multiHazard is grounded by the deterministic FEMA NRI fetch
  // inside `stampMultiHazardAndResources` (a post-compile stamp, NOT a registry
  // provider), so no registry provider-id is listed (mirrors fuelSupply's
  // stamp path). transmissionHeadroom + waterResource are honest-unavailable
  // typed sections with no in-repo provider — empty until a real fetcher lands.
  multiHazard:    [],
  transmissionHeadroom: [],
  waterResource:  ['usgs-waters'], // RD-1 (D) — refdata-only WBD + aquifers
  topography:      ['usgs-3dep-topography'],
  survey:          [],
  mineralResources: ['usgs-mrds'],
  culturalResources: ['nps-nrhp'],
  economicEvidence:  [],
};

export const SED_SECTION_LABELS: Record<SedSectionId, string> = {
  climate:        'Climate',
  waterStress:    'Water stress',
  seismic:        'Seismic',
  geotechnical:   'Geotechnical screening (SSURGO)',
  grid:           'Grid',
  flood:          'Flood',
  environmental:  'Environmental',
  nuclearInfra:   'Nuclear infra',
  solar:          'Solar',
  wind:           'Wind',
  electricity:    'Electricity',
  airQuality:     'Air quality',
  connectivity:   'Connectivity',
  parcel:         'Parcel',
  regulatory:     'Regulatory',
  siteResearch:   'Site research',
  adamsDocuments: 'ADAMS documents',
  siteProfile:    'Site profile',
  onSiteInfrastructure: 'On-site infrastructure (KMZ)',
  externalLiterature: 'Live literature (Crossref/arXiv/PubMed)',
  publicSentiment: 'Public sentiment (GDELT/Council minutes/Dockets)',
  technology:     'Reactor design (Project Intent)',
  // v1.2.1 HR-5 — BTM grounding sections.
  cooling:        'Cooling (BTM model)',
  power:          'Power semantics',
  firmCapacity:   'Firm capacity (ELCC)',
  lcoe:           'LCOE',
  fuelSupply:     'Fuel supply (natural gas)',
  // SED D2 — Interconnection + Incentives evidence sections.
  interconnection: 'Grid interconnection (ISO queue)',
  incentives:     'Incentives (IRA tax credits)',
  // SED D2 (H-6) — Multi-hazard (FEMA NRI) + transmission-headroom + water-resource.
  multiHazard:    'Multi-hazard (FEMA NRI)',
  transmissionHeadroom: 'Transmission headroom',
  waterResource:  'Water resource',
  topography:      'Topography (DEM screen)',
  survey:          'Survey / ALTA',
  mineralResources: 'Mineral resources',
  culturalResources: 'Cultural / archaeological resources',
  economicEvidence:  'Economic, workforce & comparable evidence',
};

const SECTION_IDS = Object.keys(SED_SECTION_TTL_DAYS) as SedSectionId[];

/** Placeholder per-provider cost estimate. Refined later once we
 *  collect real per-provider latency + token data. Drives the cost
 *  preview at the bottom of the refresh popover. */
export const EST_PROVIDER_COST_USD = 0.02;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function computeAgeDays(retrievedAt: string | undefined, nowMs: number): number | undefined {
  if (!retrievedAt) return undefined;
  const t = Date.parse(retrievedAt);
  if (!Number.isFinite(t)) return undefined;
  const ms = nowMs - t;
  if (ms < 0) return 0;
  return Math.floor(ms / MS_PER_DAY);
}

function classifyStaleness(
  section: EvidenceSection<unknown> | undefined,
  ageDays: number | undefined,
  ttlDays: number,
): SedSectionStaleness {
  // Treat 'unavailable' or missing slot as missing. A slot in
  // 'unavailable' carries a retrievedAt, but the data field is null
  // — the user still needs to refresh it to fill the section.
  if (!section || section.status === 'unavailable' || section.data == null) return 'missing';
  if (ageDays === undefined) return 'fresh'; // verified but no timestamp — treat as fresh, not stale
  return ageDays > ttlDays ? 'stale' : 'fresh';
}

/**
 * LLW-2 / PSE-2 — orchestrator-backed SED slots (`externalLiterature`,
 * `publicSentiment`) are NOT `EvidenceSection<T>` shapes. They use
 * `fetchedAt` instead of `retrievedAt`, `entries` instead of `data`,
 * and a tri-state `'ok' | 'partial' | 'unavailable'` status. Without
 * dedicated classifiers these slots always bucket as `'missing'` even
 * after a successful orchestrator run — the Refresh Evidence popover
 * shows "Last fetched: never · MISSING" forever.
 */
function classifyOrchestratorSection(
  slot:
    | import('../types/siteEvidence').ExternalLiteratureSection
    | import('../types/siteEvidence').PublicSentimentSection
    | undefined,
  ageDays: number | undefined,
  ttlDays: number,
): SedSectionStaleness {
  if (!slot || slot.status === 'unavailable') return 'missing';
  if (ageDays === undefined) return 'fresh';
  return ageDays > ttlDays ? 'stale' : 'fresh';
}

function isOrchestratorSection(
  slot: unknown,
): slot is
  | import('../types/siteEvidence').ExternalLiteratureSection
  | import('../types/siteEvidence').PublicSentimentSection {
  if (!slot || typeof slot !== 'object') return false;
  const status = (slot as { status?: unknown }).status;
  return status === 'ok' || status === 'partial' || status === 'unavailable';
}
function orchestratorLastFetchedAt(
  slot:
    | import('../types/siteEvidence').ExternalLiteratureSection
    | import('../types/siteEvidence').PublicSentimentSection
    | undefined,
): string | undefined {
  const fetchedAt = slot?.fetchedAt;
  return fetchedAt && fetchedAt.length > 0 ? fetchedAt : undefined;
}

export interface ComputeOptions {
  /** Override the clock — tests pass a deterministic value. */
  now?: number;
}

/**
 * Walk every SED section and emit a status row. When the SED is
 * null (site has no SED yet, eg. bootstrap not run), every row
 * comes back as 'missing' so the popover surfaces the full list.
 */
export function computeSedSectionStatuses(
  sed: SiteEvidenceDocument | null | undefined,
  opts: ComputeOptions = {},
): SedSectionStatus[] {
  const nowMs = opts.now ?? Date.now();
  const out: SedSectionStatus[] = [];
  for (const id of SECTION_IDS) {
    const ttlDays = SED_SECTION_TTL_DAYS[id];
    const providers = SED_SECTION_PROVIDERS[id] ?? [];
    const label = SED_SECTION_LABELS[id];

    // LLW-2 / PSE-2 — orchestrator slots use fetchedAt + tri-state
    // status, not EvidenceSection retrievedAt/data. Branch here so the
    // popover surfaces "Last fetched · fresh/stale/missing" correctly
    // after maybeFetchExternalLiterature / maybeFetchPublicSentiment.
    if (id === 'externalLiterature' || id === 'publicSentiment') {
      const rawSlot = id === 'externalLiterature'
        ? sed?.externalLiterature
        : sed?.publicSentiment;
      if (isOrchestratorSection(rawSlot)) {
        const orchFetchedAt = orchestratorLastFetchedAt(rawSlot);
        const orchAgeDays = computeAgeDays(orchFetchedAt, nowMs);
        const orchStaleness = classifyOrchestratorSection(rawSlot, orchAgeDays, ttlDays);
        out.push({
          id,
          label,
          lastFetchedAt: orchFetchedAt,
          ageDays: orchAgeDays,
          ttlDays,
          staleness: orchStaleness,
          providers,
        });
        continue;
      }
      // Legacy/mis-shaped slot — fall through to EvidenceSection logic.
    }

    const slot = sed
      ? (sed as unknown as Record<string, EvidenceSection<unknown> | undefined>)[id]
      : undefined;
    const lastFetchedAt = slot?.retrievedAt && slot.retrievedAt.length > 0 ? slot.retrievedAt : undefined;
    const ageDays = computeAgeDays(lastFetchedAt, nowMs);
    const staleness = classifyStaleness(slot, ageDays, ttlDays);
    out.push({ id, label, lastFetchedAt, ageDays, ttlDays, staleness, providers });
  }
  return out;
}

/**
 * Producers that refresh a section WITHOUT a registry provider id.
 *
 * `SED_SECTION_PROVIDERS` lists only `fetchAllSiteData` providers, so
 * intent-stamped and derived sections appear there as `[]` even though a
 * refresh CAN regenerate them (`topUpSiteSed` invokes these producers). This
 * map was previously inlined in the store's `refreshSedSections` as three
 * hard-coded `providers.push(...)` calls, invisible to every other caller —
 * which is why a surface reading only the registry map believes `technology`
 * cannot be refreshed.
 */
export const SED_SECTION_SYNTHETIC_PRODUCERS: Partial<Record<SedSectionId, string[]>> = {
  technology:   ['project-intent'],
  cooling:      ['project-intent'],
  power:        ['project-intent'],
  firmCapacity: ['project-intent'],
  lcoe:         ['financial-assumptions'],
};

/**
 * THE provider list a refresh of `selectedSectionIds` will actually run —
 * registry providers plus the synthetic producers above.
 *
 * One resolution shared by the store action that PERFORMS a refresh and by
 * every surface that OFFERS one, so a surface can no longer offer a refresh
 * the store will reject.
 */
export function resolveRefreshProviders(
  statuses: ReadonlyArray<SedSectionStatus>,
  selectedSectionIds: ReadonlyArray<SedSectionId>,
): string[] {
  const providers = new Set(providersForSections(statuses, selectedSectionIds));
  for (const id of selectedSectionIds) {
    for (const p of SED_SECTION_SYNTHETIC_PRODUCERS[id] ?? []) providers.add(p);
  }
  return [...providers];
}

/**
 * True when a refresh can actually regenerate this section.
 *
 * Derived from the two maps rather than listed by hand, so giving a section
 * its first provider or producer makes it refreshable everywhere at once.
 * False for genuinely un-refreshable sections: uploaded documents (`survey`),
 * server-frozen snapshots (`economicEvidence`), and derived slots with no
 * producer (`parcel`, `siteProfile`, `onSiteInfrastructure`,
 * `adamsDocuments`, `incentives`, `multiHazard`, `transmissionHeadroom`).
 *
 * The distinction is load-bearing, not cosmetic. Every refresh affordance
 * resolves a section list to a provider list, and `refreshSedSections`
 * rejects an empty one outright ("no data provider, so there is nothing to
 * re-run"). Offering a refresh for such a section is therefore a button that
 * cannot clear the state it is offered to fix.
 *
 * This is also why an un-refreshable section must not be promoted into
 * `REPORT_TYPE_SED_REQUIREMENTS`: that map's only gating consumer is the
 * preflight required-stale branch, whose sole remedy is a refresh. An
 * un-refreshable section there is a permanent, unclearable gate on every site
 * past that section's TTL.
 */
export function isProviderRefreshableSedSection(id: SedSectionId): boolean {
  return (SED_SECTION_PROVIDERS[id]?.length ?? 0) > 0
    || (SED_SECTION_SYNTHETIC_PRODUCERS[id]?.length ?? 0) > 0;
}

/**
 * Convenience: given the computed statuses + a desired set, expand
 * to the de-duplicated provider id list the store action will pass
 * to topUpSiteSed as `onlyProviders`.
 */
export function providersForSections(
  statuses: ReadonlyArray<SedSectionStatus>,
  selectedSectionIds: ReadonlyArray<SedSectionId>,
): string[] {
  const want = new Set(selectedSectionIds);
  const providers = new Set<string>();
  for (const s of statuses) {
    if (!want.has(s.id)) continue;
    for (const p of s.providers) providers.add(p);
  }
  return [...providers];
}

/* ── Whole-SED staleness (SED_Stale_Data_Rebuild SD3) ──────────────────
 *
 * Distinct from the per-section TTL machinery above: this classifies a
 * site's *entire* SED as stale vs. fresh against a fixed cutoff date —
 * the day a major upstream data-quality fix finished landing. Used by
 * the per-site "Rebuild SED" button, the AdminPanel bulk-rebuild
 * action, and the `rebuild-stale-seds` CLI script so all three share
 * one threshold.
 */

/** The day all three upstream data-quality fixes (PE — PDF text
 *  extraction; CA — provider centroid accuracy; AC+ES — doc
 *  auto-classification + AuthGate backfill) finished landing in prod.
 *  Any SED built before this is considered stale. Bump this (and re-run
 *  the bulk rebuild) the next time a major upstream change lands. */
export const STALE_CUTOFF_DATE = '2026-05-13T00:00:00Z';

/** Minimal SED-pointer shape `isStaleSed` reads — a structural subset
 *  of `SiteEntity` so callers (store, AdminPanel, CLI, tests) can pass
 *  a full site or a plain `{ sedComputedAt }` literal. */
export interface SedStalenessSite {
  /** ISO timestamp of the SED's last successful build, when known. */
  sedComputedAt?: string;
}

/* ── Stuck-bootstrap watchdog (SED_Architecture_Unification SA5) ──────
 *
 * A bootstrap is "stuck" when the user has been staring at the in-flight
 * banner long enough that the most-likely cause is a silently-dead
 * background job — not provider latency. Stuckness is a function of two
 * site-doc fields:
 *   - sedStatusStartedAt: stamped on every 'bootstrapping' flip
 *   - sedRebuildProgress.lastProgressAt: stamped by sedBootstrap's
 *     logProgress as each stage lands
 *
 * Threshold ladder (UI + backstop share definitions so they can't drift):
 *   - In-flight banner renders blue while bootstrapping AND fresh.
 *   - At >10 min total elapsed AND >5 min since last progress write,
 *     `isStuckBootstrap` flips true → banner swaps to red with a Retry
 *     button (SedRebuildBanner reads this).
 *   - After >30 min without progress or a worker heartbeat,
 *     `isAbandonedBootstrap` requests server reconciliation.
 */

/** Threshold for "no progress in N min" — banner swaps to red beyond this. */
export const STUCK_NO_PROGRESS_MS = 5 * 60 * 1000;
/** Threshold for "overall elapsed too long" — banner swaps to red beyond this. */
export const STUCK_TOTAL_ELAPSED_MS = 10 * 60 * 1000;
/** Threshold the AuthGate backstop uses to force-fail an abandoned bootstrap. */
export const STUCK_BACKSTOP_ELAPSED_MS = 30 * 60 * 1000;
/**
 * D0.1 — threshold the SERVER SITE-doc reaper (sedBootstrapRouter
 * GET /bootstrap/active/:siteId) uses to force-fail a SITE stranded at
 * `sedStatus:'bootstrapping'` with NO live lease (the job died pre-lock /
 * a trigger 503). Shared here so the server and the client read one
 * definition and cannot drift.
 *
 * 4-RUNG LADDER INVARIANT (a future edit to ANY rung must preserve the order):
 *   STUCK_TOTAL_ELAPSED_MS (10m, UI red banner)
 *     < SED_BOOTSTRAP_STALE_MS (12m, server reap)
 *       < BOOTSTRAP_WALL_CLOCK_MS (15m, client give-up, sedBootstrap.ts)
 *         < STUCK_BACKSTOP_ELAPSED_MS (30m, AuthGate backstop ceiling).
 * 12m is chosen so the server only reaps AFTER the UI red banner (10m) has
 * shown and BEFORE the client's 15m give-up — a faster heal than a 30m reload.
 * The reap is GATED ON NO-LIVE-LEASE (isLockActive false), never on elapsed
 * time alone, so a healthy slow job whose 15s heartbeat keeps the lock fresh
 * is NEVER reaped.
 */
export const SED_BOOTSTRAP_STALE_MS = 12 * 60 * 1000;
/**
 * Live-Walk Blockers LB1-2 / C9b — "heartbeat stale" soft-stall
 * threshold. Healthy SD3 doc-aware / conflict-detect heartbeats land
 * every ≤30s, but a single slow Gemini turn, provider rate-limit
 * backoff, or the 60s doc-extraction timeout can legitimately leave
 * the heartbeat quiet for over a minute. The original 90s ceiling
 * fired the yellow soft-stall banner far too eagerly — within ~one
 * tick of a single slow turn — so it's raised to 4 min (240s).
 *
 * Invariant: this MUST stay above the 60s doc-extraction timeout (so a
 * single in-flight extraction never trips it) yet STRICTLY below the
 * hard `STUCK_NO_PROGRESS_MS` (5 min = 300s) red escalation tier, so
 * `isHeartbeatStale` (soft yellow) and `isStuckBootstrap` (hard red)
 * stay mutually exclusive. 60s < 240s < 300s holds.
 */
export const HEARTBEAT_STALE_MS = 4 * 60 * 1000;

/** Minimal site shape `isStuckBootstrap` reads. */
export interface StuckBootstrapSite {
  sedStatus?: 'bootstrapping' | 'partial' | 'complete' | 'failed';
  sedStatusStartedAt?: string;
  sedBootstrapLockedAt?: string;
  sedRebuildProgress?: {
    done: number;
    total: number;
    startedAt: string;
    lastProgressAt: string;
    stage?: string;
    docTotal?: number;
    lockHeartbeatAt?: string;
  };
}

/** Extra heartbeat slack per doc during doc-aware extraction. */
export const HEARTBEAT_STALE_DOC_AWARE_MS_PER_DOC = 4 * 1000;
/** Cap on doc-aware extra slack (45 docs → 180s on top of base 90s). */
export const HEARTBEAT_STALE_DOC_AWARE_EXTRA_CAP_MS = 180 * 1000;

/**
 * Live-Walk Blockers LB1-2 — scaled heartbeat-stale threshold. Doc-aware
 * rebuilds with many documents can legitimately go quiet between Gemini
 * turns (429 backoff, per-doc extraction). Scale the soft-stall ceiling
 * so a single slow doc or rate-limit pause does not shame the user.
 */
export function heartbeatStaleThresholdMs(
  site: StuckBootstrapSite | null | undefined,
): number {
  const progress = site?.sedRebuildProgress;
  if (progress?.stage === 'doc-aware') {
    const docTotal = progress.docTotal;
    if (typeof docTotal === 'number' && docTotal > 0) {
      const extra = Math.min(
        docTotal * HEARTBEAT_STALE_DOC_AWARE_MS_PER_DOC,
        HEARTBEAT_STALE_DOC_AWARE_EXTRA_CAP_MS,
      );
      return HEARTBEAT_STALE_MS + extra;
    }
  }
  return HEARTBEAT_STALE_MS;
}

/**
 * Resolve the millisecond timestamp a bootstrap started.
 *
 * V134-BUCKET-REBUILD — the watchdogs used to read ONLY
 * `sedStatusStartedAt`. An orphaned run that lost that stamp (e.g. a
 * partial write, a listener disconnect, or a job whose 'bootstrapping'
 * flip landed `sedRebuildProgress` but not `sedStatusStartedAt`) was then
 * INVISIBLE to every stuck/abandoned check — so the 30-min backstop never
 * fired and the "SED REBUILD IN PROGRESS · 0/4 · started 34m ago" banner
 * stayed forever (the Holly Ridge symptom). The SedRebuildBanner already
 * falls back to `sedRebuildProgress.startedAt` for its "started Nm ago"
 * copy, so the watchdogs must read the SAME source or they disagree with
 * what the user sees. Returns `NaN` only when neither stamp is present.
 */
// D0.1 — exported so the server SITE-doc reaper (sedBootstrapRouter) resolves
// the bootstrap start time the SAME NaN-tolerant way the UI watchdogs do
// (sedStatusStartedAt with fallback to sedRebuildProgress.startedAt), so server
// and UI never disagree on elapsed time.
export function bootstrapStartedAtMs(site: StuckBootstrapSite): number {
  const fromStatus = site.sedStatusStartedAt ? Date.parse(site.sedStatusStartedAt) : NaN;
  if (Number.isFinite(fromStatus)) return fromStatus;
  const fromProgress = site.sedRebuildProgress?.startedAt
    ? Date.parse(site.sedRebuildProgress.startedAt)
    : NaN;
  return fromProgress;
}

/**
 * True when the site's bootstrap appears stuck — i.e. it's been
 * 'bootstrapping' for too long AND nothing has written progress
 * recently. False for any site that isn't currently bootstrapping
 * (no banner to swap to red), or for one that's making forward
 * progress.
 *
 * Both conditions must hold: a long-elapsed bootstrap that's still
 * writing progress is just slow, not stuck; a freshly-started one
 * with no progress yet is also fine.
 */
export function isStuckBootstrap(
  site: StuckBootstrapSite | null | undefined,
  nowMs: number,
): boolean {
  if (!site || site.sedStatus !== 'bootstrapping') return false;

  const startedAt = bootstrapStartedAtMs(site);
  // No start time stamped on EITHER field — we can't reason about elapsed;
  // bias false. The AuthGate backstop will eventually flip the site to
  // 'failed' if it's truly abandoned (and it stamps startedAt on retry).
  if (!Number.isFinite(startedAt)) return false;
  const elapsed = nowMs - startedAt;
  if (elapsed < STUCK_TOTAL_ELAPSED_MS) return false;

  const lastProgressAt = site.sedRebuildProgress?.lastProgressAt
    ? Date.parse(site.sedRebuildProgress.lastProgressAt)
    : NaN;
  // No progress doc at all → 10+ min in with nothing written → stuck.
  if (!Number.isFinite(lastProgressAt)) return true;
  const sinceProgress = nowMs - lastProgressAt;
  return sinceProgress >= STUCK_NO_PROGRESS_MS;
}

/**
 * Live-Walk Blockers LB1-2 — soft-stall detector. True when the
 * bootstrap is still in flight AND no heartbeat has landed for
 * `HEARTBEAT_STALE_MS` (default 90s) AND the hard `isStuckBootstrap`
 * watchdog hasn't fired yet. The intent is a middle-tier banner that
 * shows BEFORE the 5-min hard threshold:
 *
 *   ≤30s since heartbeat   → healthy (blue banner)
 *   30s–90s                → still healthy (treat as in-progress)
 *   90s–5min               → soft stall (yellow, offer Cancel-and-retry)
 *   ≥5min + 10min elapsed  → hard stuck (red, isStuckBootstrap)
 *
 * Mutually exclusive with `isStuckBootstrap` for ergonomic banner
 * branching — when the hard watchdog fires, the red banner takes
 * over and this returns false so callers can branch with a simple
 * `if (isStuck) red else if (isSoft) yellow else blue`.
 *
 * @param thresholdMs heartbeat-stale ceiling. Defaults to
 *                    `heartbeatStaleThresholdMs(site)` so doc-aware
 *                    rebuilds with many docs get a longer leash.
 *                    Tests pass an explicit override to simulate the
 *                    boundary without advancing the clock.
 */
export function isHeartbeatStale(
  site: StuckBootstrapSite | null | undefined,
  nowMs: number,
  thresholdMs?: number,
): boolean {
  const threshold = thresholdMs ?? heartbeatStaleThresholdMs(site);
  if (!site || site.sedStatus !== 'bootstrapping') return false;
  // The hard watchdog owns the >5-min branch — don't double-classify.
  if (isStuckBootstrap(site, nowMs)) return false;
  const lastProgressAt = site.sedRebuildProgress?.lastProgressAt
    ? Date.parse(site.sedRebuildProgress.lastProgressAt)
    : NaN;
  // No progress doc yet → we have nothing to compare against, fall
  // back to the start time so the user still gets a soft-stall hint
  // when the bootstrap hangs immediately after starting.
  const reference = Number.isFinite(lastProgressAt)
    ? lastProgressAt
    : (site.sedStatusStartedAt ? Date.parse(site.sedStatusStartedAt) : NaN);
  if (!Number.isFinite(reference)) return false;
  return nowMs - reference >= threshold;
}

/**
 * A long-running job is abandoned only after 30 minutes without activity.
 * A document-heavy build can legitimately take longer while its server lease
 * and document milestones continue advancing. This is a reconciliation hint;
 * the server still verifies the live lease before changing persisted status.
 */
export function isAbandonedBootstrap(
  site: StuckBootstrapSite | null | undefined,
  nowMs: number,
): boolean {
  if (!site || site.sedStatus !== 'bootstrapping') return false;
  // V134-BUCKET-REBUILD — fall back to sedRebuildProgress.startedAt so an
  // orphan that lost its sedStatusStartedAt stamp (the Holly Ridge case)
  // still gets force-failed by the AuthGate backstop at the 30-min ceiling
  // instead of staying "in progress" forever.
  const startedAt = bootstrapStartedAtMs(site);
  if (!Number.isFinite(startedAt)) return false;
  const lastActivityAt = Math.max(startedAt, ...[
    site.sedRebuildProgress?.lastProgressAt,
    site.sedRebuildProgress?.lockHeartbeatAt,
    site.sedBootstrapLockedAt,
  ].map(value => value ? Date.parse(value) : NaN).filter(Number.isFinite));
  return nowMs - lastActivityAt >= STUCK_BACKSTOP_ELAPSED_MS;
}

/**
 * RSF-3 — True when the user clicks Retry on a bootstrap that's been
 * running past the hard `STUCK_BACKSTOP_ELAPSED_MS` ceiling and we
 * should force-abort the previous run BEFORE re-firing the rebuild.
 *
 * Distinct from `isStuckBootstrap` (5-min-no-progress soft tier that
 * just swaps the banner to red) and `isAbandonedBootstrap` (next-mount
 * backstop the AuthGate fires from a different surface). This helper
 * is the one the Retry button consults so the click does the
 * cooperative-cancel + re-fire sequence atomically, instead of
 * stacking a fresh run on top of a still-running ghost (which led to
 * the original Redhead doom loop where every Retry re-paid the full
 * Gemini cost in parallel with the previous attempt).
 *
 * Manual cancellation uses total elapsed time; automatic abandonment also
 * requires stale activity. A user's explicit Cancel-and-retry must still
 * stop a long live job before starting its replacement.
 */
export function shouldForceAbortBootstrap(
  site: StuckBootstrapSite | null | undefined,
  nowMs: number,
): boolean {
  if (!site || site.sedStatus !== 'bootstrapping') return false;
  // V134-BUCKET-REBUILD — same orphan-tolerant start-time resolution as
  // isAbandonedBootstrap so the Cancel-and-retry force-abort safety also
  // fires for a >30-min ghost that only carries sedRebuildProgress.startedAt.
  const startedAt = bootstrapStartedAtMs(site);
  if (!Number.isFinite(startedAt)) return false;
  return nowMs - startedAt >= STUCK_BACKSTOP_ELAPSED_MS;
}

/**
 * True when the site's SED predates `cutoff` — including the case where
 * `sedComputedAt` is missing entirely (legacy data the SD1 backfill
 * hasn't reached yet, or a site whose SED build long predates the
 * field). Comparing ISO 8601 strings lexicographically is correct
 * because they're zero-padded and UTC-normalised.
 *
 * @param site   the site (or `{ sedComputedAt }`) to classify
 * @param cutoff the cutoff ISO timestamp; defaults to STALE_CUTOFF_DATE
 */
export function isStaleSed(
  site: SedStalenessSite | null | undefined,
  cutoff: string = STALE_CUTOFF_DATE,
): boolean {
  const stamp = site?.sedComputedAt;
  if (!stamp) return true; // no timestamp ⇒ treat as stale
  return stamp < cutoff;
}

export interface BulkRebuildOptions {
  /** Delay between successive rebuilds, ms. Spreads provider load.
   *  The first site fires immediately; each subsequent site waits this
   *  long after the previous one's rebuild *started*. */
  throttleMs?: number;
  /** Invoked after each site's rebuild settles (success or failure). */
  onProgress?: (processed: number, total: number, siteId: string, error?: unknown) => void;
}

const DEFAULT_BULK_THROTTLE_MS = 5_000;

/**
 * Sequentially rebuild a list of sites' SEDs, throttled at
 * `throttleMs`/site. Generic over the rebuild callback so the
 * AdminPanel passes `rebuildSiteSed` and tests pass a spy. The first
 * site fires immediately; subsequent sites wait `throttleMs` between
 * the previous rebuild *starting* and the next one. A rebuild that
 * throws is swallowed (reported via `onProgress`) so one bad site
 * doesn't stall the rest of the batch.
 */
export async function bulkRebuildStaleSeds(
  siteIds: ReadonlyArray<string>,
  rebuild: (siteId: string) => Promise<void>,
  opts: BulkRebuildOptions = {},
): Promise<{ processed: number; failed: number }> {
  const throttleMs = opts.throttleMs ?? DEFAULT_BULK_THROTTLE_MS;
  const total = siteIds.length;
  let processed = 0;
  let failed = 0;
  for (let i = 0; i < siteIds.length; i++) {
    const siteId = siteIds[i];
    if (i > 0 && throttleMs > 0) {
      await new Promise<void>(r => setTimeout(r, throttleMs));
    }
    let err: unknown;
    try {
      await rebuild(siteId);
    } catch (e) {
      err = e;
      failed++;
    }
    processed++;
    opts.onProgress?.(processed, total, siteId, err);
  }
  return { processed, failed };
}

/* ── SED rebuild suppression flags ────────────────────────────────────
 *
 * Per-site re-entrancy flags set by the `rebuildSiteSed` store action
 * around a rebuild: fields the rebuild itself flips (sedId cleared,
 * sedStatus changes, etc.) shouldn't be treated as user-driven
 * staleness while the rebuild is in flight. Inline freshness surfaces
 * (evidence toolbar, SedRebuildBanner, preflight cards) read site
 * state directly and are unaffected.
 *
 * The floating "Site evidence is now out of date / Rebuild SED →"
 * toast that used to consume these flags was removed in founder QA
 * round 3 (the inline affordances above are the staleness surface);
 * the flags stay because the store's rebuild lifecycle still sets and
 * clears them.
 */
const suppressedSites = new Set<string>();

export function setSedStalenessSuppressed(siteId: string, suppressed: boolean): void {
  if (suppressed) {
    suppressedSites.add(siteId);
  } else {
    suppressedSites.delete(siteId);
  }
}

export function isSedStalenessSuppressed(siteId: string): boolean {
  return suppressedSites.has(siteId);
}

/**
 * Sections the "Refresh sections" button can actually act on: everything
 * with a live provider, plus the intent-stamped specials refreshSedSections
 * maps by hand (technology/cooling/power/firmCapacity → 'project-intent',
 * lcoe → 'financial-assumptions'). The refresh planner filters to this set
 * so the currency banner never demands work the button cannot perform —
 * provider-less sections (parcel, siteProfile, …) previously made
 * "N never collected" a permanent fixture no refresh could clear.
 */
export const USER_REFRESHABLE_SECTIONS: ReadonlySet<string> = new Set([
  ...Object.entries(SED_SECTION_PROVIDERS)
    .filter(([, providers]) => providers.length > 0)
    .map(([sectionId]) => sectionId),
  'technology', 'cooling', 'power', 'firmCapacity', 'lcoe',
]);
